import React from 'react';
import type { StageNotifications } from './types';
import { Sparkles } from 'lucide-react';
import {
  ApprovalCard,
  ApprovalCardContent,
} from '../shared/approval';

interface Step4NotificationsProps {
  data?: StageNotifications;
  onChange?: (data: StageNotifications) => void;
  stageData?: import('./types').StageData;
  onUpdate?: (updates: Partial<import('./types').StageData>) => void;
}

export const Step4Notifications: React.FC<Step4NotificationsProps> = ({ data, onChange, stageData, onUpdate }) => {
  return (
    <div className="h-full flex flex-col items-center justify-center p-8">
      <ApprovalCard variant="elevated" className="max-w-2xl w-full">
        <ApprovalCardContent className="p-12 text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-xl"></div>
              <div className="relative bg-gradient-to-br from-blue-500 to-purple-500 p-4 rounded-full">
                <Sparkles className="w-12 h-12 text-white" />
              </div>
            </div>
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold text-foreground mb-3">
            Notifications
          </h2>
          
          {/* Subtitle */}
          <p className="text-xl text-muted-foreground mb-8">
            Coming Soon
          </p>

          {/* Description */}
          <div className="bg-muted/50 rounded-lg p-6 mb-8">
            <p className="text-base text-muted-foreground leading-relaxed">
              We're building an advanced notification system that will allow you to configure 
              custom alerts and actions when timesheets are approved or rejected. Stay tuned 
              for this exciting feature!
            </p>
          </div>

          {/* Feature Preview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center">
                  <span className="text-green-500 text-lg">✓</span>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-foreground mb-1">Email Notifications</h3>
                  <p className="text-xs text-muted-foreground">Send automated emails to approvers and submitters</p>
                </div>
              </div>
            </div>

            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <span className="text-blue-500 text-lg">🔔</span>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-foreground mb-1">In-App Alerts</h3>
                  <p className="text-xs text-muted-foreground">Real-time notifications within the application</p>
                </div>
              </div>
            </div>

            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-500/10 rounded-lg flex items-center justify-center">
                  <span className="text-orange-500 text-lg">⚡</span>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-foreground mb-1">Custom Actions</h3>
                  <p className="text-xs text-muted-foreground">Trigger webhooks and custom workflows</p>
                </div>
              </div>
            </div>

            <div className="bg-background border border-border rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center">
                  <span className="text-purple-500 text-lg">📊</span>
                </div>
                <div>
                  <h3 className="font-semibold text-sm text-foreground mb-1">Activity Logs</h3>
                  <p className="text-xs text-muted-foreground">Track all notification events and history</p>
                </div>
              </div>
            </div>
          </div>
        </ApprovalCardContent>
      </ApprovalCard>
    </div>
  );
};
